import { useState, useCallback } from 'react'
import { useAsyncOperation } from './useAsyncOperation'
import { reviewsAPI } from '../services/api/reviews'
import { toast } from '../services/toastService'
import { createLogger } from '../utils/logger'

const logger = createLogger('useReviewForm')

export function useReviewForm({ entityId, onSuccess, t }) {
  const [rating, setRating] = useState(0)
  const [content, setContent] = useState('')
  const [editingReview, setEditingReview] = useState(null)
  const { loading, execute } = useAsyncOperation()

  const startEditing = useCallback((review) => {
    setEditingReview(review)
    setRating(review.rating || 0)
    setContent(review.content || '')
  }, [])

  const cancelEditing = useCallback(() => {
    setEditingReview(null)
    setRating(0)
    setContent('')
  }, [])

  const submit = useCallback(async () => {
    if (!rating && !content.trim()) return
    if (editingReview) {
      await execute(
        () => reviewsAPI.updateReview(editingReview.id, {
          ...(rating ? { rating } : {}),
          ...(content.trim() ? { content: content.trim() } : {})
        }),
        {
          onSuccess: () => {
            cancelEditing()
            onSuccess?.()
          },
          successMessage: t('review.reviewUpdated', 'Review updated!')
        }
      )
    } else {
      await execute(
        () => reviewsAPI.createReview(entityId, {
          ...(rating ? { rating } : {}),
          ...(content.trim() ? { content: content.trim() } : {})
        }),
        {
          onSuccess: () => {
            setRating(0)
            setContent('')
            onSuccess?.()
          },
          onError: (err) => {
            const code = err?.response?.data?.error?.code
            if (code === 'proof_of_presence_required') {
              toast.error(t('review.popRequired', 'Spot or confirm this entity first before leaving a review'))
            } else {
              logger.error('Failed to submit review', err)
              toast.error(t('review.submitError', 'Could not submit review. Please try again.'))
            }
          },
          successMessage: t('review.reviewSubmitted', 'Review submitted!')
        }
      )
    }
  }, [rating, content, editingReview, entityId, execute, cancelEditing, onSuccess, t])

  const deleteReview = useCallback(async (reviewId) => {
    await execute(
      () => reviewsAPI.deleteReview(reviewId),
      {
        onSuccess: () => onSuccess?.(),
        successMessage: t('review.reviewDeleted', 'Review deleted')
      }
    )
  }, [execute, onSuccess, t])

  return {
    rating,
    setRating,
    content,
    setContent,
    editingReview,
    startEditing,
    cancelEditing,
    submit,
    deleteReview,
    loading
  }
}
